import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { KeyRound, Mail, ArrowLeft, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { apiFetch } from '../utils/api';


export default function ForgotPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isSuperAdmin = searchParams.get('sa') === '1';

  const [step, setStep] = useState(1); // 1=email, 2=code, 3=done
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSendCode = async () => {
    if (!email) return toast.error('Εισάγετε email');
    setLoading(true);
    try {
      const url = isSuperAdmin
        ? `/api/super-admin/forgot-password`
        : `/api/auth/admin-forgot-password`;
      const body = isSuperAdmin ? { username, email } : { email };
      const res = await apiFetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      toast.success(data.message || 'Ελέγξτε το email σας');
      setStep(2);
    } catch {
      toast.error('Σφάλμα σύνδεσης');
    }
    setLoading(false);
  };

  const handleResetPassword = async () => {
    if (!code || !newPassword) return toast.error('Συμπληρώστε τα πεδία');
    setLoading(true);
    try {
      const url = isSuperAdmin
        ? `/api/super-admin/reset-password`
        : `/api/auth/admin-reset-password`;
      const res = await apiFetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, code, new_password: newPassword }),
      });
      if (res.ok) {
        toast.success('Ο κωδικός άλλαξε!');
        setStep(3);
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Σφάλμα');
      }
    } catch {
      toast.error('Σφάλμα σύνδεσης');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-slate-100 dark:bg-slate-950 flex items-center justify-center px-4">
      <Card className="w-full max-w-md p-8 dark:bg-slate-900 dark:border-slate-700">
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-[#004C98] rounded-full flex items-center justify-center mx-auto mb-4">
            {isSuperAdmin ? <ShieldCheck className="text-white" size={28} /> : <KeyRound className="text-white" size={28} />}
          </div>
          <h1 className="text-xl font-bold text-slate-800 dark:text-white">
            {isSuperAdmin ? 'Super Admin Recovery (2-Email)' : 'Ανάκτηση Κωδικού Admin'}
          </h1>
          {isSuperAdmin && (
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
              Απαιτείται ένα από τα 2 emails ανάκτησης που έχετε ορίσει
            </p>
          )}
        </div>

        {step === 1 && (
          <div className="space-y-4">
            {isSuperAdmin && (
              <div>
                <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Username</label>
                <Input value={username} onChange={e => setUsername(e.target.value)} placeholder="username" className="h-11 mt-1 dark:bg-slate-800 dark:border-slate-600 dark:text-white" data-testid="recovery-username" />
              </div>
            )}
            <div>
              <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Email Ανάκτησης</label>
              <Input value={email} onChange={e => setEmail(e.target.value)} placeholder="admin@example.com" type="email" className="h-11 mt-1 dark:bg-slate-800 dark:border-slate-600 dark:text-white" data-testid="recovery-email" />
            </div>
            <Button onClick={handleSendCode} className="w-full h-11 bg-[#004C98] hover:bg-[#003A75]" disabled={loading} data-testid="send-code-btn">
              <Mail size={16} className="mr-2" /> Αποστολή Κωδικού
            </Button>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            <p className="text-sm text-slate-600 dark:text-slate-400 text-center">Εισάγετε τον 6-ψήφιο κωδικό που λάβατε στο <strong>{email}</strong></p>
            <Input value={code} onChange={e => setCode(e.target.value)} placeholder="000000" maxLength={6} className="h-14 text-center text-2xl tracking-widest font-bold dark:bg-slate-800 dark:border-slate-600 dark:text-white" data-testid="recovery-code" />
            <div>
              <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Νέος Κωδικός</label>
              <Input value={newPassword} onChange={e => setNewPassword(e.target.value)} type="password" placeholder="Τουλάχιστον 6 χαρακτήρες" className="h-11 mt-1 dark:bg-slate-800 dark:border-slate-600 dark:text-white" data-testid="new-password" />
            </div>
            <Button onClick={handleResetPassword} className="w-full h-11 bg-green-600 hover:bg-green-700" disabled={loading} data-testid="reset-password-btn">
              Αλλαγή Κωδικού
            </Button>
          </div>
        )}

        {step === 3 && (
          <div className="text-center space-y-4">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
              <ShieldCheck className="text-green-600" size={28} />
            </div>
            <p className="text-lg font-bold text-green-700">Ο κωδικός άλλαξε!</p>
            <Button onClick={() => navigate('/login')} className="w-full h-11 bg-[#004C98] hover:bg-[#003A75]">
              Σύνδεση
            </Button>
          </div>
        )}

        <div className="mt-6 text-center">
          <button onClick={() => navigate('/login')} className="text-sm text-[#004C98] hover:underline flex items-center gap-1 mx-auto">
            <ArrowLeft size={14} /> Επιστροφή στη σύνδεση
          </button>
        </div>
      </Card>
    </div>
  );
}
